'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import {
  Banknote,
  Check,
  ClipboardList,
  FileText,
  Globe,
  PencilRuler,
} from 'lucide-react';
import { PackUsageNotice } from '@/features/subscription';
import { NoticeBanner } from '@/shared/ui/NoticeBanner';
import { Textarea } from '@/shared/ui/shadcn/textarea';
import { SettingsCard, SettingsItem } from '@/shared/ui/settings';
import {
  NestedSheet,
  NestedSheetContent,
  NestedSheetHeader,
  NestedSheetTitle,
  NestedSheetBody,
} from '@/shared/ui/NestedSheet';
import { cn } from '@/shared/lib/cn';
import { sortAlbumLocalesForUser, getLocaleByCode } from '@/shared/constants';
import {
  isPremiumAlbumTemplate,
  MATERIALS_SECTION_TOGGLE_ROW,
  SECONDARY_SECTION_TOGGLE_ROWS,
  type AlbumMeasurementSystem,
  type AlbumSectionToggleKey,
  type AlbumSectionToggleRow,
  type AlbumSectionToggles,
  type AlbumTemplate,
} from '@/features/design/album';
import type { AlbumEstimatePicker } from '../hooks/use-album-estimate-picker';
import { AlbumEstimateOptions } from './AlbumEstimateOptions';
import { AlbumRenderThumbnailStrip } from './AlbumRenderThumbnailStrip';

// Render order for the template (design) axis — `standard` free, the rest are
// premium (Pro-tier). Mirrors Apple/Android catalog order.
const ALBUM_TEMPLATE_OPTIONS: readonly AlbumTemplate[] = [
  'standard',
  'architect',
  'studio',
];

// Temporarily hide the Template picker: only `standard` exists today, so the
// row adds no choice. The default `standard` is still sent to the API. Flip
// back to `true` once `architect`/`studio` ship. Mirrors Apple/Android.
const TEMPLATE_PICKER_ENABLED = false;

// Units picker order — Auto (server decides, default) first, then the two
// explicit systems. Mirrors Apple/Android.
const ALBUM_MEASUREMENT_SYSTEM_OPTIONS: readonly AlbumMeasurementSystem[] = [
  'auto',
  'metric',
  'imperial',
];

interface AlbumPanelSettingsProps {
  // Requirements
  requirements: string;
  onRequirementsChange: (value: string) => void;
  isArchived: boolean;

  // Template (design axis — only `standard` selectable)
  selectedTemplate: AlbumTemplate;
  onTemplateChange: (template: AlbumTemplate) => void;

  // Content-section toggles (materials / blueprints / preparation guide /
  // safety requirements / building regulations) — free, all default ON.
  sectionToggles: AlbumSectionToggles;
  onSectionToggleChange: (
    key: AlbumSectionToggleKey,
    enabled: boolean
  ) => void;

  // Language
  selectedLanguage: string;
  onLanguageChange: (language: string) => void;

  // Units (measurement system) — free, default Auto (server decides; the
  // field is omitted from the generate request).
  measurementSystem: AlbumMeasurementSystem;
  onMeasurementSystemChange: (system: AlbumMeasurementSystem) => void;

  // Partial renders warning (rendered below cost-disclaimer to match Apple).
  hasPartialRenders: boolean;
  renderedCount: number;
  totalCount: number;

  // Thumbnails of the renders that will be packed into the album — shown at the
  // top of the panel so the selection is visible before any setting.
  selectedRenderUrls: string[];

  // Cost-estimate picker (FE4): this design's estimates first (default),
  // "New estimate", then sibling designs' estimates greyed at the end.
  estimatePicker: AlbumEstimatePicker;

  // State
  isGenerating: boolean;

  // Server-localized significant-cost warning (GET /album/cost-prediction
  // `warning_message`) — null when the predicted cost is below the
  // server-side threshold, while a prediction is loading, or on error. The
  // client never composes this text (API-driven messages rule).
  costWarningMessage: string | null;
}

export function AlbumPanelSettings({
  requirements,
  onRequirementsChange,
  isArchived,
  selectedTemplate,
  onTemplateChange,
  sectionToggles,
  onSectionToggleChange,
  selectedLanguage,
  onLanguageChange,
  measurementSystem,
  onMeasurementSystemChange,
  hasPartialRenders,
  renderedCount,
  totalCount,
  selectedRenderUrls,
  estimatePicker,
  isGenerating,
  costWarningMessage,
}: AlbumPanelSettingsProps) {
  const t = useTranslations('album');
  const tEstimate = useTranslations('common.estimate');

  const [isTemplateOpen, setIsTemplateOpen] = useState(false);
  const [isRequirementsOpen, setIsRequirementsOpen] = useState(false);
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const [isEstimateOpen, setIsEstimateOpen] = useState(false);
  const [isUnitsOpen, setIsUnitsOpen] = useState(false);

  // Cost-estimate picker (FE4) — resolve the row's trailing label: the
  // localized document name when one is picked (estimates carry no user-set
  // title — Apple/Android row-value parity), "New estimate", or "Don't
  // include". No client-side plan gate post-pivot — the server returns the
  // album paywall on Generate for non-pack users.
  const {
    currentDesignEstimates,
    otherDesignEstimates,
    selectedEstimateId,
    setSelectedEstimateId,
    isEstimateIncluded,
    excludeEstimate,
  } = estimatePicker;
  const selectedEstimate = [
    ...currentDesignEstimates,
    ...otherDesignEstimates,
  ].find((e) => e.id === selectedEstimateId);
  const estimateRowValue = !isEstimateIncluded
    ? tEstimate('album.dontInclude')
    : selectedEstimate
      ? tEstimate('card.untitled')
      : tEstimate('album.generateNew');
  // Sorted once when sheet opens — stable during selection
  const [languageSheetOrder, setLanguageSheetOrder] = useState(() =>
    sortAlbumLocalesForUser(selectedLanguage)
  );
  const selectedLanguageName =
    getLocaleByCode(selectedLanguage)?.name ?? selectedLanguage;

  const openLanguageSheet = () => {
    setLanguageSheetOrder(sortAlbumLocalesForUser(selectedLanguage));
    setIsLanguageOpen(true);
  };

  // Content-section toggle row — free Switch row with a trailing divider.
  // Rendered in two slices (Materials first, the rest after the Estimate
  // row), so the markup lives in one place.
  const renderSectionToggleRow = ({
    key,
    icon: Icon,
    labelKey,
  }: AlbumSectionToggleRow) => (
    <div key={key}>
      <SettingsItem
        icon={Icon}
        title={t(`settings.${labelKey}.title`)}
        disabled={isGenerating}
        toggle={{
          checked: sectionToggles[key],
          onCheckedChange: (checked) => onSectionToggleChange(key, checked),
        }}
      />
      <div className="border-t border-border/30 mx-5" />
    </div>
  );

  return (
    // No horizontal padding: this panel renders only on the single-column
    // concept page, which already supplies the page gutter (px-4 sm:px-6
    // lg:px-8). An inner px-3 doubled it — the settings sat narrower than the
    // sibling albums list and the Estimate tab. `relative` hosts the
    // drill-down NestedSheets (their own px-4 is unaffected).
    <div className="relative space-y-3 py-3">
      {/* Single soft ~80% pack-usage warning (albums) — server-driven `low`
          band on the building's pack, no counters (pivot rule). Renders
          nothing off the `low` band. */}
      <PackUsageNotice kind="albums" />

      {/* Renders that will be packed into the album — shown right after the
          heading so the selection is visible before any setting. */}
      <AlbumRenderThumbnailStrip urls={selectedRenderUrls} />

      {/* Partial-renders warning — shown only when some angles still lack a
          render, so the user knows the album won't cover every view. */}
      {hasPartialRenders && (
        <NoticeBanner
          variant="info"
          message={t('partialRendersWarning', { renderedCount, totalCount })}
        />
      )}

      {/* Settings group */}
      <SettingsCard>
        {/* Template Row (design axis) — temporarily hidden (see
            TEMPLATE_PICKER_ENABLED). */}
        {TEMPLATE_PICKER_ENABLED && (
          <>
            <SettingsItem
              icon={FileText}
              title={t('settings.type.label')}
              disabled={isGenerating}
              onClick={() => setIsTemplateOpen(true)}
              value={t(`settings.type.${selectedTemplate}`)}
            />

            <div className="border-t border-border/30 mx-5" />
          </>
        )}

        {/* Language Row */}
        <SettingsItem
          icon={Globe}
          title={t('settings.language')}
          disabled={isGenerating}
          onClick={openLanguageSheet}
          value={selectedLanguageName}
        />

        <div className="border-t border-border/30 mx-5" />

        {/* Content-section toggles — materials / blueprints / preparation
            guide / safety requirements / building regulations. Free Switch
            rows, all default ON; `include_blueprints` gates ONLY the facade
            projections. The Estimate row sits right after Materials —
            order mirrors Apple/Android. */}
        {renderSectionToggleRow(MATERIALS_SECTION_TOGGLE_ROW)}

        {/* Estimate Row (FE4) — defaults to this design's latest ready
            estimate; the sheet also offers "Don't include", "New estimate"
            and sibling designs' estimates. Always opens the picker — the
            server returns the album paywall on Generate for non-pack users. */}
        <SettingsItem
          icon={Banknote}
          title={tEstimate('album.label')}
          disabled={isGenerating}
          onClick={() => setIsEstimateOpen(true)}
          value={estimateRowValue}
        />

        <div className="border-t border-border/30 mx-5" />

        {SECONDARY_SECTION_TOGGLE_ROWS.map(renderSectionToggleRow)}

        {/* Requirements — shapes WHAT the album contains, so it sits in the
            Settings group between the content-section toggles and Units (Apple
            parity; mirrors the two-column AlbumTabContent). Always opens the
            requirements editor. */}
        {!isArchived && (
          <>
            <SettingsItem
              icon={ClipboardList}
              title={t('settings.requirements.label')}
              onClick={() => setIsRequirementsOpen(true)}
              value={
                requirements
                  ? `${requirements.substring(0, 40)}${requirements.length > 40 ? '...' : ''}`
                  : undefined
              }
            />

            <div className="border-t border-border/30 mx-5" />
          </>
        )}

        {/* Units Row — measurement system for the album content (Auto /
            Metric / Imperial). Free for everyone; Auto (default) lets the
            server pick and omits the field from the generate request. Last
            Settings-group row — mirrors Apple/Android. */}
        <SettingsItem
          icon={PencilRuler}
          title={t('settings.units.label')}
          disabled={isGenerating}
          onClick={() => setIsUnitsOpen(true)}
          value={t(`settings.units.${measurementSystem}`)}
        />
      </SettingsCard>

      {/* Significant-cost warning — server-computed flag + server-localized
          text (parity with Apple): shown right before the Generate button only
          when the current settings make generation token-expensive. */}
      {costWarningMessage != null && !isGenerating && (
        <NoticeBanner variant="warning" message={costWarningMessage} />
      )}

      {/* Template NestedSheet — temporarily hidden (see TEMPLATE_PICKER_ENABLED). */}
      {TEMPLATE_PICKER_ENABLED && (
      <NestedSheet open={isTemplateOpen} onOpenChange={setIsTemplateOpen}>
        <NestedSheetContent>
          <NestedSheetHeader>
            <NestedSheetTitle>{t('settings.type.label')}</NestedSheetTitle>
          </NestedSheetHeader>
          <NestedSheetBody className="px-4">
            {/* Template = design axis. Only `standard` is selectable;
                `architect`/`studio` are "Coming soon" — disabled, no paywall
                on tap (product decision post-pivot). Mirrors Apple/Android. */}
            <div className="space-y-2">
              {ALBUM_TEMPLATE_OPTIONS.map((option) => {
                const isComingSoon = isPremiumAlbumTemplate(option);
                const isSelected = !isComingSoon && selectedTemplate === option;
                return (
                  <button
                    key={option}
                    type="button"
                    onClick={() => {
                      // "Coming soon" templates are inert — never select, never
                      // paywall. Mirrors Apple/Android.
                      if (isComingSoon) return;
                      onTemplateChange(option);
                      setIsTemplateOpen(false);
                    }}
                    disabled={isGenerating || isComingSoon}
                    className={cn(
                      'w-full flex flex-col gap-1 p-4 rounded-lg border-2 transition-colors text-start',
                      isSelected
                        ? 'border-primary bg-primary/5'
                        : 'border-border',
                      isComingSoon ? 'opacity-50' : 'hover:bg-muted/50'
                    )}
                  >
                    <div className="flex items-center justify-between w-full gap-2">
                      <span className="text-list-item-subtitle">
                        {t(`settings.type.${option}`)}
                      </span>
                      {/* Quiet "Coming soon" marker on the unreleased
                          templates — not a Pass badge, no paywall. */}
                      {isComingSoon && (
                        <span className="inline-flex items-center rounded-full bg-muted px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wide leading-none text-muted-foreground whitespace-nowrap shrink-0">
                          {t('settings.type.comingSoon')}
                        </span>
                      )}
                      {isSelected && (
                        <Check className="h-4 w-4 text-primary flex-shrink-0" />
                      )}
                    </div>
                    {option === 'standard' && (
                      <span className="text-card-meta text-muted-foreground">
                        {t('settings.type.standardDescription')}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </NestedSheetBody>
        </NestedSheetContent>
      </NestedSheet>
      )}

      {/* Requirements NestedSheet */}
      <NestedSheet
        open={isRequirementsOpen}
        onOpenChange={setIsRequirementsOpen}
      >
        <NestedSheetContent>
          <NestedSheetHeader>
            <NestedSheetTitle>
              {t('settings.requirements.label')}
            </NestedSheetTitle>
          </NestedSheetHeader>
          <NestedSheetBody className="p-4">
            <Textarea
              value={requirements}
              onChange={(e) => onRequirementsChange(e.target.value)}
              placeholder={t('settings.requirements.placeholder')}
              className="min-h-[200px] resize-none"
              maxLength={2000}
              disabled={isArchived || isGenerating}
            />
          </NestedSheetBody>
        </NestedSheetContent>
      </NestedSheet>

      {/* Estimate NestedSheet (FE4) — this design's estimates, "New estimate",
          then sibling designs' estimates greyed at the end. */}
      <NestedSheet open={isEstimateOpen} onOpenChange={setIsEstimateOpen}>
        <NestedSheetContent>
          <NestedSheetHeader>
            <NestedSheetTitle>{tEstimate('album.label')}</NestedSheetTitle>
          </NestedSheetHeader>
          <NestedSheetBody className="px-4">
            <AlbumEstimateOptions
              currentDesignEstimates={currentDesignEstimates}
              otherDesignEstimates={otherDesignEstimates}
              selectedEstimateId={selectedEstimateId}
              isEstimateIncluded={isEstimateIncluded}
              onSelect={(id) => {
                setSelectedEstimateId(id);
                setIsEstimateOpen(false);
              }}
              onExclude={() => {
                excludeEstimate();
                setIsEstimateOpen(false);
              }}
              disabled={isGenerating}
            />
          </NestedSheetBody>
        </NestedSheetContent>
      </NestedSheet>

      {/* Language NestedSheet */}
      <NestedSheet open={isLanguageOpen} onOpenChange={setIsLanguageOpen}>
        <NestedSheetContent>
          <NestedSheetHeader>
            <NestedSheetTitle>{t('settings.language')}</NestedSheetTitle>
          </NestedSheetHeader>
          <NestedSheetBody className="px-4">
            <div className="space-y-2">
              {languageSheetOrder.map((lang) => {
                const isSelected = lang.code === selectedLanguage;
                return (
                  <button
                    key={lang.code}
                    onClick={() => {
                      onLanguageChange(lang.code);
                      setIsLanguageOpen(false);
                    }}
                    className={cn(
                      'w-full flex items-center justify-between p-4 rounded-lg border-2 transition-colors text-start',
                      isSelected
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:bg-muted/50'
                    )}
                  >
                    <span className="text-list-item-subtitle">
                      {lang.name} ({lang.nativeName})
                    </span>
                    {isSelected && (
                      <Check className="h-4 w-4 text-primary flex-shrink-0" />
                    )}
                  </button>
                );
              })}
            </div>
          </NestedSheetBody>
        </NestedSheetContent>
      </NestedSheet>

      {/* Units NestedSheet — Auto (server decides) / Metric / Imperial. */}
      <NestedSheet open={isUnitsOpen} onOpenChange={setIsUnitsOpen}>
        <NestedSheetContent>
          <NestedSheetHeader>
            <NestedSheetTitle>{t('settings.units.label')}</NestedSheetTitle>
          </NestedSheetHeader>
          <NestedSheetBody className="px-4">
            <div className="space-y-2">
              {ALBUM_MEASUREMENT_SYSTEM_OPTIONS.map((option) => {
                const isSelected = measurementSystem === option;
                return (
                  <button
                    key={option}
                    type="button"
                    onClick={() => {
                      onMeasurementSystemChange(option);
                      setIsUnitsOpen(false);
                    }}
                    disabled={isGenerating}
                    className={cn(
                      'w-full flex items-center justify-between p-4 rounded-lg border-2 transition-colors text-start',
                      isSelected
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:bg-muted/50'
                    )}
                  >
                    <span className="text-list-item-subtitle">
                      {t(`settings.units.${option}`)}
                    </span>
                    {isSelected && (
                      <Check className="h-4 w-4 text-primary flex-shrink-0" />
                    )}
                  </button>
                );
              })}
            </div>
          </NestedSheetBody>
        </NestedSheetContent>
      </NestedSheet>
    </div>
  );
}
